import * as signalR from '@microsoft/signalr';
import { UiKeys } from '@voicedojo/backend-types';
import { derived, writable, type Readable } from 'svelte/store';
import { t } from '$lib/i18n';
import { toast } from '$lib/stores/notifications';
import { apiOrigin } from './client';

// Never null: SignalR's default gives up after four attempts, for good.
export function retryDelayMs(attempt: number): number {
  return Math.min(30_000, 1_000 * 2 ** attempt) * (0.5 + Math.random() * 0.5);
}

// No negotiate request: nothing a load balancer could send to another replica.
export function hubConnection(path: string, withCredentials: boolean): signalR.HubConnection {
  return new signalR.HubConnectionBuilder()
    .withUrl(`${apiOrigin()}${path}`, {
      withCredentials,
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: ({ previousRetryCount }) => retryDelayMs(previousRetryCount),
    })
    .build();
}

const downHubs = writable<ReadonlySet<string>>(new Set());

export function reportHubLive(hub: string, live: boolean): void {
  downHubs.update((down) => {
    if (live !== down.has(hub)) return down;
    const next = new Set(down);
    if (live) next.delete(hub);
    else next.add(hub);
    return next;
  });
}

export const liveUpdatesPaused: Readable<boolean> = derived(downHubs, (down) => down.size > 0);

// The grace keeps a reconnect blip from flashing the toast; the quiet window keeps a
// flaky network from toasting every outage.
export function toastWhileLiveUpdatesPaused(graceMs = 5_000, quietMs = 3_600_000): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let shown: number | null = null;
  let lastShownAt = -Infinity;
  const unsubscribe = liveUpdatesPaused.subscribe((paused) => {
    if (paused) {
      timer ??= setTimeout(() => {
        if (Date.now() - lastShownAt < quietMs) return;
        lastShownAt = Date.now();
        shown = toast.sticky(t(UiKeys.Toast.LiveUpdatesPaused));
      }, graceMs);
      return;
    }
    clearTimeout(timer);
    timer = undefined;
    if (shown === null) return;
    toast.dismiss(shown);
    shown = null;
    toast.info(t(UiKeys.Toast.LiveUpdatesBack));
  });
  return () => {
    unsubscribe();
    clearTimeout(timer);
  };
}
